import { useEffect, useState } from 'react';
import type {
  ApiClient, SanitaerGegenstandsuebersicht, SanitaerAnlagen,
} from '../api';
import {
  ContentSection, DataTable, EmptyState, ErrorState, ExportLeiste, KpiCard, KpiReihe,
  LoadingState, StatusBadge, formatiereZahl, type ExportZiel, type Spalte,
} from '../muster';
import { PageHeader } from '../rahmen/PageHeader';

/**
 * Zwei Gesamtübersichten: die Gegenstände je Etage und die Anlagen.
 *
 * ═══ Wozu die Gegenstandsübersicht dient ═════════════════════════════════════
 *
 * Zur VERIFIZIERUNG des Spitzendurchflusses für das ganze Projekt.
 *
 * Die Trinkwasserberechnung ermittelt V̇s aus der Summe aller
 * Berechnungsdurchflüsse. Diese Zahl ist eine einzige — und eine einzige Zahl
 * lässt sich nicht prüfen. Erst wenn danebensteht, aus wie vielen WCs,
 * Waschtischen und Duschen sie entstanden ist, kann jemand sagen, ob sie
 * stimmt.
 *
 * Die Summenzeile unten ist die HAUSANSCHLUSSSUMME: alles, was am
 * Gebäudeeintritt ankommt.
 *
 * ═══ Warum je Etage ══════════════════════════════════════════════════════════
 *
 * 1.400 Räume sind keine Übersicht. Sieben Etagen sind eine — und die Etage ist
 * die Ebene, auf der ein Planer ein Gebäude prüft.
 */

interface Eigenschaften {
  api: ApiClient;
  projektId: string;
}

export function SanitaerGegenstandsseite({ api, projektId }: Eigenschaften) {
  const [daten, setDaten] = useState<SanitaerGegenstandsuebersicht | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [art, setArt] = useState('');
  const [, setSeite] = useState(1);

  useEffect(() => {
    api.sanitaerGegenstandsuebersicht(projektId)
      .then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  // TS9a (TX-STANDARD S1.9): PDF und Excel in der Exportleiste des Kopfs; ein Fehler beim Erzeugen steht an der
  // Leiste und nimmt nicht mehr die ganze Seite weg.
  const ohneBestand = !daten || (daten.gegenstaende ?? []).length === 0
    ? { grund: 'Noch keine eingestuften Sanitärgegenstände.' } : undefined;
  const ziele: ExportZiel[] = [
    {
      art: 'pdf', beschriftung: 'PDF Vollständige Liste',
      laden: () => api.sanitaerUebersichtPdf(projektId, 'gegenstaende'), gesperrt: ohneBestand,
    },
    { art: 'excel', beschriftung: 'Excel', laden: () => api.sanitaerGegenstaendeExcel(projektId), gesperrt: ohneBestand },
  ];
  const kopf = <PageHeader titel="Sanitärgegenstände" aktionen={<ExportLeiste ziele={ziele} />} />;

  if (fehler) {
    return (
      <>
        {kopf}
        <ErrorState
          meldung="Die Gegenstandsübersicht konnte nicht geladen werden."
          technisch={fehler}
        />
      </>
    );
  }

  if (!daten) return <>{kopf}<LoadingState text="Gegenstände werden geladen …" zeilen={10} spalten={6} /></>;

  const g = daten.gesamt;
  const gegenstaende = daten.gegenstaende ?? [];
  const artenGesamt = daten.artenGesamt ?? [];

  const gefiltert = gegenstaende.filter((x) => (art ? x.art === art : true));

  const artspalten: Spalte<any>[] = [
    {
      schluessel: 'art', titel: 'Art', immerSichtbar: true,
      wert: (a) => `${a.bezeichnung} ${a.art}`,
      inhalt: (a) => (
        <>
          <strong>{a.bezeichnung}</strong> <span className="schwach">({a.art})</span>
        </>
      ),
    },
    { schluessel: 'objekte', titel: 'Objekte', art: 'zahl', wert: (a) => a.objekte },
    {
      schluessel: 'plaetze', titel: 'Zapfstellen / Plätze', art: 'zahl',
      wert: (a) => a.plaetze,
    },
  ];

  const listenspalten: Spalte<any>[] = [
    {
      schluessel: 'art', titel: 'Art', immerSichtbar: true,
      wert: (x) => x.art ?? 'ohne Art',
      inhalt: (x) => <strong>{x.art ?? 'ohne Art'}</strong>,
    },
    {
      schluessel: 'familie', titel: 'Familie / Typ',
      wert: (x) => `${x.familie ?? ''} ${x.typ ?? ''}`.trim(),
      inhalt: (x) => (
        <>
          {x.familie ?? '—'}<br />
          <span className="schwach">{x.typ ?? ''}</span>
        </>
      ),
    },
    {
      schluessel: 'ort', titel: 'Geschoss / Raum',
      wert: (x) => `${x.geschoss ?? ''} ${x.raumnummer ?? ''} ${x.raumname ?? ''}`.trim(),
      inhalt: (x) => (
        <>
          {x.geschoss ?? 'ohne Geschoss'}
          {x.modellebene && x.modellebene !== x.geschoss && (
            <span className="schwach" title="Bezeichnung im verknüpften Modell">
              {' '}({x.modellebene})
            </span>
          )}
          <br />
          <span className="schwach">{x.raumnummer ?? '—'} {x.raumname ?? ''}</span>
        </>
      ),
    },
    {
      schluessel: 'modell', titel: 'Herkunftsmodell', wert: (x) => x.herkunftsmodell,
      inhalt: (x) => <span className="schwach">{x.herkunftsmodell ?? '—'}</span>,
    },
    { schluessel: 'plaetze', titel: 'Plätze', art: 'zahl', wert: (x) => x.plaetze ?? 1 },
    {
      schluessel: 'du', titel: 'Anschlusswert', einheit: 'DU', art: 'zahl',
      wert: (x) => x.anschlusswertDU,
    },
    {
      schluessel: 'vollstrom', titel: 'V̇R kalt / warm', einheit: 'l/s',
      ausrichtung: 'rechts', wert: (x) => x.vollstromKaltLs,
      inhalt: (x) => <>{x.vollstromKaltLs ?? '—'} / {x.vollstromWarmLs ?? '—'}</>,
    },
    { schluessel: 'revitId', titel: 'Revit-ID', art: 'zahl', wert: (x) => x.revitElementId },
  ];

  return (
    <>
      {kopf}
      <ContentSection
        titel="Sanitärgegenstände über alle Etagen"
        beschreibung={'Grundlage für die Prüfung des Spitzendurchflusses. Die '
          + 'Summenzeile ist die Hausanschlusssumme — alles, was am Gebäudeeintritt '
          + 'ankommt. Gerechnet wird je PLATZ, nicht je Objekt: Eine Waschrinne mit '
          + 'vier Plätzen zählt viermal.'}
      >
        <KpiReihe>
          <KpiCard titel="Objekte" wert={g.objekte} />
          <KpiCard titel="Zapfstellen" wert={g.plaetze} />
          <KpiCard titel="Summe Anschlusswerte" wert={g.summeDu} einheit="l/s"
                   fussnote={(daten.doppelverknuepfungen ?? []).length > 0
                     ? `${daten.normen.abwasser} · davon doppelt verknüpft: `
                       + `${formatiereZahl(Math.round((daten.doppelverknuepfungen ?? [])
                            .reduce((s2, d) => s2 + (d.summeMehrfach ?? 0), 0) * 100) / 100)} DU`
                     : daten.normen.abwasser} />
          <KpiCard titel="Schmutzwasserabfluss" wert={g.abflussLs} einheit="l/s"
                   vergleich={`K = ${g.abflusskennzahlK}`} />
        </KpiReihe>

        <KpiReihe>
          <KpiCard titel="Summe Berechnungsdurchfluss kalt" wert={g.summeKaltLs} einheit="l/s" />
          <KpiCard titel="Spitzendurchfluss kalt" wert={g.spitzeKaltLs} einheit="l/s"
                   fussnote={daten.normen.trinkwasser} />
          <KpiCard titel="Summe Berechnungsdurchfluss warm" wert={g.summeWarmLs} einheit="l/s" />
          <KpiCard titel="Spitzendurchfluss warm" wert={g.spitzeWarmLs} einheit="l/s"
                   fussnote={daten.normen.trinkwasser} />
        </KpiReihe>

        <p className="schwach" style={{ fontSize: 12, marginBottom: 0 }}>
          Trinkwasser nach {daten.normen.trinkwasser}
          {g.kurve && <> · {g.kurve} (Gebäudeart {g.gebaeudeart})</>} ·
          Abwasser nach {daten.normen.abwasser} mit K = {g.abflusskennzahlK}
        </p>

        {/* ── Doppelt verknüpft (PB44) ──────────────────────────────────
            Diese Seite dient der PRÜFUNG des Spitzendurchflusses. Steht ein
            Modell zweimal da, gehen seine Gegenstände zweimal in jede Summe
            ein — und zwei Zahlen, die beide doppelt zählen, stimmen
            miteinander überein, ohne richtig zu sein. Deshalb steht der
            Vermerk hier oben und nicht in einer Fußnote. */}
        {(daten.doppelverknuepfungen ?? []).map((d) => (
          <p key={`${d.ablage}-${d.modell}`} style={{ marginBottom: 8 }}>
            <StatusBadge zustand="fehler" text={d.vermerk} />
          </p>
        ))}

        {/* Der Hinweis, ohne den die Zahl falsch gelesen wird. */}
        <p style={{ marginBottom: 0 }}>
          <StatusBadge
            zustand="warnung"
            text={'Der Spitzendurchfluss der Gesamtsumme ist NICHT die Summe der '
              + 'Spitzendurchflüsse je Etage: Die Gleichzeitigkeit ist nicht linear.'}
          />
        </p>
      </ContentSection>

      <ContentSection
        titel="Vollständigkeit nach Gegenstandsart"
        beschreibung={'Alle aktiven, als Sanitär eingestuften Objekte — unabhängig '
          + 'davon, ob Geschoss oder Raum im verknüpften Modell gepflegt sind.'}
        meta={`${formatiereZahl(g.objekte)} Objekte · ${formatiereZahl(g.plaetze)} Zapfstellen`}
        nackt
      >
        <DataTable
          name="Gegenstandsarten"
          spalten={artspalten}
          zeilen={artenGesamt}
          schluessel={(a) => String(a.art)}
          ausfuhr
          ausfuhrDateiname="gegenstandsarten"
          dicht
        />
      </ContentSection>

      <ContentSection titel="Gegenstandsliste" nackt>
        {/* Das eigene Blättern (200 je Seite) entfällt: `DataTable`
            virtualisiert ab 200 Zeilen und zeigt trotzdem alle (U6b). */}
        <DataTable
          name="Gegenstände"
          spalten={listenspalten}
          zeilen={gefiltert}
          schluessel={(x) => String(x.id)}
          suchbegriff={suche}
          beiSuche={setSuche}
          suchePlatzhalter="WC, Urinal, Familie, Raum, Revit-ID …"
          trefferText={art
            ? `${formatiereZahl(gefiltert.length)} von ${formatiereZahl(gegenstaende.length)} Objekten`
            : undefined}
          ausfuhr
          ausfuhrDateiname="sanitaergegenstaende"
          dicht
          werkzeuge={(
            <select value={art} onChange={(e) => { setArt(e.target.value); setSeite(1); }}
                    aria-label="Gegenstandsart" style={{ width: 'auto' }}>
              <option value="">alle Arten</option>
              {artenGesamt.map((a) => (
                <option key={a.art} value={a.art}>{a.bezeichnung} ({a.objekte})</option>
              ))}
            </select>
          )}
          leer={(
            <EmptyState
              titel={art || suche
                ? 'Kein Gegenstand passt zu diesem Filter.'
                : 'Für dieses Modell ist kein Sanitärgegenstand aufgenommen.'}
              erklaerung={art || suche
                ? 'Art und Suche schränken die Liste ein.'
                : 'Die Gegenstände kommen aus den verknüpften Architektur- und Küchenmodellen.'}
              aktion={(art || suche) ? (
                <button className="m-knopf" onClick={() => { setArt(''); setSuche(''); }}>
                  Filter aufheben
                </button>
              ) : undefined}
            />
          )}
        />
      </ContentSection>

      {(daten.etagen ?? []).map((e) => (
        <ContentSection
          key={e.etage}
          titel={e.etage}
          meta={`${e.objekte} Objekte · ${e.plaetze} Zapfstellen`}
          aufklappbar
          standardOffen={false}
          nackt
        >
          <DataTable
            name={`Gegenstände ${e.etage}`}
            spalten={[
              { schluessel: 'art', titel: 'Art', immerSichtbar: true, wert: (a: any) => a.bezeichnung },
              { schluessel: 'objekte', titel: 'Objekte', art: 'zahl', wert: (a: any) => a.objekte },
              {
                schluessel: 'plaetze', titel: 'Zapfstellen', art: 'zahl',
                titelHinweis: 'Mehr Plätze als Objekte bedeutet Reihenwaschtische',
                wert: (a: any) => a.plaetze,
              },
              { schluessel: 'duJePlatz', titel: 'Anschlusswert je Platz', einheit: 'DU', art: 'zahl', wert: (a: any) => a.duJePlatz },
              { schluessel: 'summeDu', titel: 'Summe Anschlusswerte', einheit: 'DU', art: 'zahl', wert: (a: any) => a.summeDu },
              { schluessel: 'kaltJePlatz', titel: 'V̇R kalt je Platz', einheit: 'l/s', art: 'zahl', wert: (a: any) => a.kaltJePlatz },
              { schluessel: 'summeKalt', titel: 'Summe V̇R kalt', einheit: 'l/s', art: 'zahl', wert: (a: any) => a.summeKaltLs },
              { schluessel: 'summeWarm', titel: 'Summe V̇R warm', einheit: 'l/s', art: 'zahl', wert: (a: any) => a.summeWarmLs },
              {
                schluessel: 'norm', titel: 'Norm', wert: (a: any) => a.norm,
                inhalt: (a: any) => (
                  <span className="schwach" style={{ fontSize: 11 }}>{a.norm ?? ''}</span>
                ),
              },
            ]}
            zeilen={e.arten}
            schluessel={(a: any) => String(a.art)}
            suche={false}
            dicht
          />
        </ContentSection>
      ))}
    </>
  );
}

/**
 * Die Anlagen: Fettabscheider, Hebeanlagen, Vorbehälter,
 * Frischwasserstationen und die Regenentwässerung.
 *
 * Jede mit Raum, Etage und Bauteilverweis — und mit dem Vergleich zwischen
 * dem, was der Fachplaner ausgelegt hat, und dem, was im Modell steht.
 */
export function SanitaerAnlagenseite({ api, projektId }: Eigenschaften) {
  const [daten, setDaten] = useState<SanitaerAnlagen | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [pdfLaeuft, setPdfLaeuft] = useState(false);

  useEffect(() => {
    api.sanitaerAnlagen(projektId)
      .then(setDaten)
      .catch((e) => setFehler(e instanceof Error ? e.message : String(e)));
  }, [api, projektId]);

  const pdf = async () => {
    setPdfLaeuft(true);
    try {
      await api.sanitaerUebersichtPdf(projektId, 'anlagen');
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setPdfLaeuft(false);
    }
  };

  if (fehler) {
    return (
      <ErrorState
        meldung="Die Anlagenübersicht konnte nicht geladen werden."
        technisch={fehler}
      />
    );
  }

  if (!daten) return <LoadingState text="Anlagen werden geladen …" zeilen={8} spalten={6} />;

  const fettspalten: Spalte<any>[] = [
    { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung },
    { schluessel: 'qs', titel: 'Schmutzwasserabfluss', einheit: 'l/s', art: 'zahl', wert: (a) => a.qsLs },
    { schluessel: 'fd', titel: 'Dichtebeiwert fd', art: 'zahl', wert: (a) => a.fd },
    { schluessel: 'ft', titel: 'Temperaturbeiwert ft', art: 'zahl', wert: (a) => a.ft },
    { schluessel: 'fr', titel: 'Reinigungsbeiwert fr', art: 'zahl', wert: (a) => a.fr },
    { schluessel: 'ngG', titel: 'Nenngröße gerechnet', art: 'zahl', wert: (a) => a.ngGerechnet },
    {
      schluessel: 'ngW', titel: 'Nenngröße gewählt', art: 'zahl',
      wert: (a) => a.ngGewaehlt,
      inhalt: (a) => <strong>{a.ngGewaehlt}</strong>,
    },
    { schluessel: 'schlamm', titel: 'Schlammfang', einheit: 'l', art: 'zahl', wert: (a) => a.schlammfangL },
    { schluessel: 'fett', titel: 'Fettspeicher', einheit: 'l', art: 'zahl', wert: (a) => a.fettspeicherL },
    { schluessel: 'geraete', titel: 'Geräte', art: 'zahl', wert: (a) => a.gegenstaendeGesamt },
    {
      schluessel: 'norm', titel: 'Norm', wert: (a) => a.norm,
      inhalt: (a) => <span className="schwach" style={{ fontSize: 11 }}>{a.norm}</span>,
    },
  ];

  const hebespalten: Spalte<any>[] = [
    {
      schluessel: 'befund', titel: 'Befund', art: 'status', breite: '150px',
      wert: (a) => (a.befund ?? 'ohne Befund'),
      inhalt: (a) => (a.befund
        ? <StatusBadge zustand="warnung" text="Befund" klein titel={a.befund} />
        : <StatusBadge zustand="gut" text="ohne Befund" klein />),
    },
    { schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung },
    { schluessel: 'typ', titel: 'Typ', wert: (a) => a.typ },
    {
      schluessel: 'kreis', titel: 'Kreis', wert: (a) => a.kreis,
      inhalt: (a) => <span className="schwach">{a.kreis ?? '—'}</span>,
    },
    { schluessel: 'zulauf', titel: 'Zulauf', einheit: 'l/s', art: 'zahl', wert: (a) => a.zulaufLs },
    { schluessel: 'foerderstrom', titel: 'Förderstrom', einheit: 'l/s', art: 'zahl', wert: (a) => a.foerderstromLs },
    { schluessel: 'hmin', titel: 'Mindestförderhöhe', einheit: 'm', art: 'zahl', wert: (a) => a.mindestfoerderhoeheM },
    { schluessel: 'h', titel: 'Förderhöhe', einheit: 'm', art: 'zahl', wert: (a) => a.foerderhoeheM },
    { schluessel: 'dn', titel: 'Druckleitung', einheit: 'DN', art: 'zahl', wert: (a) => a.druckleitungDn },
    { schluessel: 'v', titel: 'Geschwindigkeit', einheit: 'm/s', art: 'zahl', wert: (a) => a.geschwindigkeitMs },
    { schluessel: 'nutzvol', titel: 'Nutzvolumen', einheit: 'l', art: 'zahl', wert: (a) => a.nutzvolumenL },
    { schluessel: 'p2', titel: 'Wellenleistung P2', einheit: 'kW', art: 'zahl', wert: (a) => a.p2Kw },
  ];

  const deaSpalten: Spalte<any>[] = [
    {
      schluessel: 'kennung', titel: 'Kennung', immerSichtbar: true, wert: (a) => a.kennung,
      inhalt: (a) => <strong>{a.kennung}</strong>,
    },
    { schluessel: 'zone', titel: 'Zone', wert: (a) => a.bezeichnung },
    {
      schluessel: 'typ', titel: 'Typ',
      wert: (a) => [a.hersteller, a.typ].filter(Boolean).join(' '),
      inhalt: (a) => <>{a.hersteller ? `${a.hersteller} ${a.typ ?? ''}`.trim() : '—'}</>,
    },
    { schluessel: 'zapfstellen', titel: 'Zapfstellen', art: 'zahl', wert: (a) => a.zapfstellen },
    { schluessel: 'spitze', titel: 'Spitzendurchfluss', einheit: 'l/s', art: 'zahl', wert: (a) => a.spitzeLs },
    { schluessel: 'q', titel: 'Förderstrom', einheit: 'm³/h', art: 'zahl', wert: (a) => a.foerderstromM3h },
    { schluessel: 'geod', titel: 'geodätisch', einheit: 'm', art: 'zahl', wert: (a) => a.geodaetischM },
    { schluessel: 'reibung', titel: 'Reibung', einheit: 'm', art: 'zahl', wert: (a) => a.reibungM },
    { schluessel: 'erf', titel: 'erforderlich', einheit: 'm', art: 'zahl', wert: (a) => a.erforderlichM },
    { schluessel: 'pv', titel: 'Versorgungsdruck', einheit: 'm', art: 'zahl', wert: (a) => a.versorgungsdruckM },
    {
      schluessel: 'h', titel: 'Förderhöhe', einheit: 'm', art: 'zahl',
      wert: (a) => a.foerderhoeheM,
      inhalt: (a) => <strong>{a.foerderhoeheM ?? '—'}</strong>,
    },
  ];

  const regenspalten: Spalte<any>[] = [
    { schluessel: 'bereich', titel: 'Bereich', immerSichtbar: true, wert: (r) => r.bereich },
    { schluessel: 'flaeche', titel: 'Fläche', einheit: 'm²', art: 'zahl', wert: (r) => r.flaecheM2 },
    { schluessel: 'erf', titel: 'erforderlich', einheit: 'm³', art: 'zahl', wert: (r) => r.erforderlichM3 },
    {
      schluessel: 'netto', titel: 'netto', einheit: 'm³', art: 'zahl',
      wert: (r) => r.nettoM3,
      inhalt: (r) => <strong>{r.nettoM3 ?? '—'}</strong>,
    },
    { schluessel: 'versickerung', titel: 'Versickerung', einheit: 'l/s', art: 'zahl', wert: (r) => r.versickerrateLs },
    { schluessel: 'dauer', titel: 'Regendauer', einheit: 'min', art: 'zahl', wert: (r) => r.regendauerMin },
    { schluessel: 'spende', titel: 'Regenspende', einheit: 'l/(s·ha)', art: 'zahl', wert: (r) => r.regenspendeLsHa },
    { schluessel: 'te', titel: 'Entleerungszeit', einheit: 'h', art: 'zahl', wert: (r) => r.entleerungszeitH },
    {
      schluessel: 'norm', titel: 'Verfahren', wert: (r) => r.norm,
      inhalt: (r) => <span className="schwach" style={{ fontSize: 11 }}>{r.norm}</span>,
    },
  ];

  return (
    <>
      <ContentSection
        titel="Anlagenübersicht"
        beschreibung={'Was der Fachplaner ausgelegt hat und was im Modell steht — je '
          + 'Anlage mit Raum-, Etagen- und Bauteilverweis.'}
        aktionen={(
          <button className="m-knopf m-knopf-haupt" onClick={pdf} disabled={pdfLaeuft}>
            {pdfLaeuft ? 'wird erzeugt …' : 'Als PDF'}
          </button>
        )}
      >
        <span />
      </ContentSection>

      {/* --- Die Anlagenkennzeichen, in BEIDE Richtungen (Paket S8) --- */}
      <Aksabschnitt aks={daten.aks} />

      {/* --- Fettabscheider --- */}
      <ContentSection
        titel="Fettabscheider"
        meta={`${daten.fettabscheider.auslegungen.length} ausgelegt · ${daten.fettabscheider.imModell.length} im Modell`}
      >
        <DataTable
          name="Auslegung Fettabscheider"
          spalten={fettspalten}
          zeilen={daten.fettabscheider.auslegungen}
          schluessel={(a: any) => String(a.kennung)}
          ausfuhr
          ausfuhrDateiname="fettabscheider"
          dicht
          leer={(
            <EmptyState
              titel="Kein Fettabscheider ausgelegt."
              erklaerung="Die Auslegung entsteht aus der Fettabwasserberechnung."
            />
          )}
        />

        <Modellliste titel="Im Modell" zeilen={daten.fettabscheider.imModell} />
      </ContentSection>

      {/* --- Hebeanlagen und Vorbehälter --- */}
      {([
        ['Hebeanlagen', daten.hebeanlagen] as const,
        ['Vorbehälteranlagen', daten.vorbehaelter] as const,
      ]).map(([titel, gruppe]) => (
        <ContentSection
          key={titel}
          titel={titel}
          meta={`${gruppe.auslegungen.length} ausgelegt · ${gruppe.imModell.length} im Modell`}
        >
          <DataTable
            name={`Auslegung ${titel}`}
            spalten={hebespalten}
            zeilen={gruppe.auslegungen}
            schluessel={(a: any) => String(a.kennung)}
            zeilenKlasse={(a: any) => (a.befund ? 'datenfeld' : undefined)}
            ausfuhr
            ausfuhrDateiname={titel.toLowerCase()}
            dicht
            leer={(
              <EmptyState
                titel={`Keine ${titel} ausgelegt.`}
                erklaerung="Die Auslegung entsteht aus der Schmutzwasserberechnung."
              />
            )}
          />

          {gruppe.auslegungen.some((a) => a.befund) && (
            <div style={{ marginTop: 10 }}>
              {gruppe.auslegungen.filter((a) => a.befund).map((a) => (
                <p key={a.kennung} style={{ margin: '4px 0' }}>
                  <StatusBadge zustand="warnung" text={`${a.kennung}: ${a.befund}`} />
                </p>
              ))}
            </div>
          )}

          <Modellliste titel="Im Modell" zeilen={gruppe.imModell} />
        </ContentSection>
      ))}

      {/* --- Druckerhöhung --- */}
      {daten.druckerhoehung.anlagen.length > 0 && (
        <ContentSection
          titel="Druckerhöhung"
          meta={`${daten.druckerhoehung.anlagen.length} Anlagen · max. ${formatiereZahl(daten.druckerhoehung.foerderhoeheGesamtM == null ? null : Math.round(daten.druckerhoehung.foerderhoeheGesamtM * 10) / 10)} m`}
          beschreibung={'Gerechnet, nicht geliefert: Aus geodätischer Höhe, '
            + 'Mindestfließdruck und Reibung folgt die Förderhöhe, aus dem '
            + 'Spitzendurchfluss der Zone die Baugröße. Liefert der Fachplaner später '
            + 'eine Anlage, steht sie daneben — nicht darüber.'}
        >
          <DataTable
            name="Druckerhöhungsanlagen"
            spalten={deaSpalten}
            zeilen={daten.druckerhoehung.anlagen}
            schluessel={(a: any) => String(a.kennung)}
            ausfuhr
            ausfuhrDateiname="druckerhoehung"
            dicht
          />

          {daten.druckerhoehung.anlagen.filter((a) => a.befund).map((a) => (
            <p key={a.kennung} style={{ margin: '4px 0' }}>
              <StatusBadge zustand="warnung" text={`${a.kennung}: ${a.befund}`} />
            </p>
          ))}

          <Modellliste titel="Im Modell" zeilen={daten.druckerhoehung.imModell} />
        </ContentSection>
      )}

      {/* --- Frischwasserstationen und Durchlauferhitzer --- */}
      {([
        ['Frischwasserstationen', daten.frischwasserstationen] as const,
        ['Durchlauferhitzer', daten.durchlauferhitzer] as const,
      ]).map(([titel, gruppe]) => (
        <ContentSection key={titel} titel={titel} meta={`${gruppe.imModell.length} im Modell`}>
          <Modellliste titel="" zeilen={gruppe.imModell} />
        </ContentSection>
      ))}

      {/* --- Regenwasser --- */}
      <ContentSection
        titel="Regenentwässerung"
        meta={`${daten.regenwasser.rigolen.length} Rigolen · ${formatiereZahl(daten.regenwasser.flaecheGesamtM2)} m² · ${formatiereZahl(daten.regenwasser.speicherGesamtM3)} m³`}
        nackt
      >
        <DataTable
          name="Rigolen"
          spalten={regenspalten}
          zeilen={daten.regenwasser.rigolen}
          schluessel={(r: any) => String(r.bereich)}
          ausfuhr
          ausfuhrDateiname="regenwasser"
          dicht
          leer={(
            <EmptyState
              titel="Keine Rigole ausgelegt."
              erklaerung="Die Auslegung entsteht aus der Regenwasserberechnung."
            />
          )}
        />
      </ContentSection>
    </>
  );
}

/**
 * Die AKS-Bilanz: beide Richtungen der Grenze, getrennt ausgewiesen
 * (F39/F54, Paket S8).
 *
 * Eine Prozentzahl („96,8 % adressiert") beantwortet keine der beiden
 * Fragen. Getrennt stehen deshalb nebeneinander: was in die AKS gehört und
 * keine trägt (LÜCKE, gehört gezählt) und was nach F39 gar keine bekommt
 * (kein Mangel und kein Nennerposten).
 */
function Aksabschnitt({ aks }: { aks: SanitaerAnlagen['aks'] }) {
  // Ein leeres Umfangsverzeichnis nennt jedes Bauteil „nicht entschieden" —
  // das wäre eine Aussage über die Datenbank und nicht über die Bauteile.
  // Sie steht deshalb da, statt als stiller Nullwert durchzugehen.
  if (!aks) return null;

  const spalten: Spalte<any>[] = [
    { schluessel: 'bauart', titel: 'Bauart', immerSichtbar: true, wert: (z) => z.bauart },
    { schluessel: 'bestand', titel: 'Bestand', art: 'zahl', wert: (z) => z.bestand },
    { schluessel: 'mit', titel: 'mit Adresse', art: 'zahl', wert: (z) => z.mitAdresse },
    {
      schluessel: 'luecke', titel: 'Lücke', art: 'zahl', wert: (z) => z.luecke,
      inhalt: (z) => (z.luecke > 0
        ? <strong>{z.luecke}</strong>
        : <span className="schwach">0</span>),
    },
    {
      schluessel: 'einstufung', titel: 'Einstufung', art: 'status', breite: '170px',
      wert: (z) => z.einstufung,
      inhalt: (z) => {
        const fall = AKS_FAELLE[z.einstufung]
          ?? { text: z.einstufung, zustand: 'warnung' as const };
        return <StatusBadge zustand={fall.zustand} text={fall.text} klein titel={z.grund} />;
      },
    },
    { schluessel: 'festlegung', titel: 'Festlegung', wert: (z) => z.festlegung },
    {
      schluessel: 'grund', titel: 'Grund', wert: (z) => z.grund,
      inhalt: (z) => <span className="schwach" style={{ fontSize: 11 }}>{z.grund}</span>,
    },
  ];

  return (
    <ContentSection
      titel="Anlagenkennzeichen (AKS)"
      beschreibung={'Die Grenze steht in msr_aks_umfang (F39/F54) und wird hier '
        + 'gelesen, nicht nachformuliert. „Lücke" zählt nur, was in die AKS '
        + 'gehört und keine trägt; „bekommt keine" ist nach F39 kein Mangel — '
        + 'Rohre, Formteile, Verbindungen und Sanitärgegenstände tragen kein '
        + 'Typenschild.'}
      meta={`${aks.verzeichniszeilen} Zeilen im Umfangsverzeichnis`}
    >
      <KpiReihe>
        <KpiCard titel="im Umfang" wert={aks.imUmfang} />
        <KpiCard titel="mit Adresse" wert={aks.mitAdresse} />
        <KpiCard
          titel="Lücken"
          wert={aks.luecke}
          zustand={aks.luecke > 0 ? 'warnung' : 'gut'}
          zustandText={aks.luecke > 0 ? 'offen' : 'keine'}
        />
        <KpiCard titel="bekommt keine (F39)" wert={aks.bekommtKeine} />
        <KpiCard
          titel="nicht entschieden"
          wert={aks.nichtEntschieden}
          zustand={aks.nichtEntschieden > 0 ? 'warnung' : 'gut'}
          zustandText={aks.nichtEntschieden > 0
            ? 'Bauart fehlt im Verzeichnis' : 'alle entschieden'}
        />
      </KpiReihe>

      <DataTable
        name="AKS-Bilanz"
        spalten={spalten}
        zeilen={aks.zeilen}
        schluessel={(z: any) => String(z.bauart)}
        ausfuhr
        ausfuhrDateiname="aks-bilanz-sanitaer"
        dicht
        leer={(
          <EmptyState
            titel="Keine Bauart im Bestand."
            erklaerung="Ohne Sanitärbauteile gibt es auch keine Adressen."
          />
        )}
      />
    </ContentSection>
  );
}

/**
 * Wie ein Fall in der AKS-Spalte heißt und was er bedeutet (Paket S8).
 *
 * Die zwei mittleren sehen gleich aus und sind es nicht: „bekommt keine"
 * ist nach F39 KEIN Mangel (Rohre, Formteile, Verbindungen,
 * Sanitärgegenstände tragen kein Typenschild), „fehlt" ist eine echte
 * Lücke. Eine leere Zelle für beide erzeugte 41.041 Arbeitslistenzeilen,
 * die PB42 gerade entfernt hat.
 */
export const AKS_FAELLE: Record<string, { text: string; zustand: 'gut' | 'warnung' | 'neutral' }> = {
  vergeben: { text: 'vergeben', zustand: 'gut' },
  fehlt: { text: 'fehlt', zustand: 'warnung' },
  bekommt_keine: { text: 'bekommt keine', zustand: 'neutral' },
  ueber_dem_umfang: { text: 'über dem Umfang', zustand: 'warnung' },
  nicht_entschieden: { text: 'nicht entschieden', zustand: 'warnung' },
};

/** Die Adresse, oder der Grund — als Zelle. */
function Akszelle({ z }: { z: { aksSchluessel: string | null; aksEinstufung: string; aksGrund: string } }) {
  if (z.aksSchluessel) {
    return <span style={{ fontFamily: 'var(--schrift-fest, monospace)', fontSize: 12 }}
                 title={z.aksGrund}>{z.aksSchluessel}</span>;
  }

  const fall = AKS_FAELLE[z.aksEinstufung]
    ?? { text: z.aksEinstufung, zustand: 'warnung' as const };

  // Der Grund steht im `title` UND als Text daneben — ein Hinweis, den man
  // nur mit der Maus findet, ist im Ausdruck und im Ausfuhrblatt weg.
  return (
    <span title={z.aksGrund}>
      <StatusBadge zustand={fall.zustand} text={fall.text} klein titel={z.aksGrund} />
    </span>
  );
}

/** Die Geräte im Modell — mit Raum-, Etagen- und Bauteilverweis. */
function Modellliste({ titel, zeilen }: {
  titel: string;
  zeilen: SanitaerAnlagen['hebeanlagen']['imModell'];
}) {
  if (zeilen.length === 0) return null;

  return (
    <DataTable
      name={titel || 'Im Modell'}
      spalten={[
        { schluessel: 'elementId', titel: 'ElementId', immerSichtbar: true, wert: (z: any) => z.revitElementId },
        { schluessel: 'typ', titel: 'Typ', wert: (z: any) => z.typ },
        {
          schluessel: 'produkt', titel: 'Hersteller · Reihe',
          wert: (z: any) => [z.hersteller, z.produktreihe].filter(Boolean).join(' · '),
          inhalt: (z: any) => (z.hersteller && z.hersteller !== 'offen'
            ? <>{z.hersteller} · {z.produktreihe}</>
            : <em className="schwach">{z.produktreihe ?? '—'}</em>),
        },
        { schluessel: 'raum', titel: 'Raum', wert: (z: any) => z.raumnummer },
        {
          schluessel: 'raumname', titel: 'Raumname', wert: (z: any) => z.raumname,
          inhalt: (z: any) => <span className="schwach">{z.raumname ?? '—'}</span>,
        },
        { schluessel: 'geschoss', titel: 'Geschoss', wert: (z: any) => z.geschoss },
        {
          // Die Adresse — oder der GRUND. Nie eine leere Zelle: Sie bedeutet
          // hier dreierlei (Lücke, „bekommt keine" nach F39, nicht
          // entschieden), und wer sie liest, kann die drei nicht trennen.
          schluessel: 'aks', titel: 'AKS', immerSichtbar: true,
          wert: (z: any) => z.aksSchluessel ?? z.aksEinstufung,
          inhalt: (z: any) => <Akszelle z={z} />,
        },
      ]}
      zeilen={zeilen}
      schluessel={(z: any) => String(z.id)}
      suche={false}
      spaltenwahl={false}
      dicht
    />
  );
}
